<template>
  <v-app>
    <app-header />

    <v-main>
      <router-view />
    </v-main>

    <app-footer />
    <service-modal />
    <contact-modal />

    <!-- Mobile Sidebar -->
    <v-navigation-drawer
      v-model="eventBus.sidebarOpen"
      location="right"
      temporary
      :width="windowWidth"
      :order="-1"
      color="#1CA5BE"
    >
      <div class="sidebar-content">
        <button class="sidebar-close" @click="eventBus.sidebarOpen = false">
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path d="M1 1L17 17M17 1L1 17" stroke="#2E2E2E" stroke-width="2" stroke-linecap="round" />
          </svg>
        </button>

        <nav class="sidebar-nav">
          <a
            v-for="link in navLinks"
            :key="link.href"
            :href="link.href === '#top' ? '#' : link.href"
            class="sidebar-link"
            :class="{ active: activeLink === link.href }"
            @click.prevent="onNavClick(link.href)"
          >
            <span class="sidebar-link-text">{{ link.label }}</span>
            <span v-if="activeLink === link.href" class="sidebar-dot">●</span>
          </a>
        </nav>

        <div class="sidebar-bottom">
          <button class="sidebar-contact-btn" @click="onContactClick">{{ $t('nav.contact') }}</button>
          <v-menu v-model="langMenuOpen" :offset="[0, 8]" location="top end">
            <template #activator="{ props }">
              <button class="sidebar-lang-btn" v-bind="props">{{ currentLangLabel }}</button>
            </template>
            <div class="lang-dropdown">
              <button
                v-for="lang in langs"
                :key="lang.code"
                class="lang-option"
                :class="{ active: currentLangLabel === lang.code }"
                @click="selectLang(lang.code)"
              >{{ lang.label }}</button>
            </div>
          </v-menu>
        </div>
      </div>
    </v-navigation-drawer>
  </v-app>
  <cookie-consent />
  <div class="lang-overlay" :class="{ visible: langOverlay }"></div>
</template>

<script>
import AppHeader from './components/AppHeader.vue'
import AppFooter from './components/12.AppFooter.vue'
import ServiceModal from './components/ServiceModal.vue'
import ContactModal from './components/ContactModal.vue'
import CookieConsent from './components/CookieConsent.vue'
import { eventBus } from '@/eventBus'
import i18n from '@/i18n'

export default {
  name: 'App',
  components: {
    AppHeader,
    AppFooter,
    ServiceModal,
    ContactModal,
    CookieConsent
  },
  data() {
    return {
      eventBus,
      activeLink: '#top',
      windowWidth: window.innerWidth,
      langMenuOpen: false,
      langOverlay: false,
      langs: [
        { code: 'En', label: 'English' },
        { code: 'Ro', label: 'Română' },
        { code: 'Ru', label: 'Русский' }
      ]
    }
  },
  computed: {
    currentLangLabel() {
      const map = { ro: 'Ro', en: 'En', ru: 'Ru' }
      return map[i18n.global.locale.value] || 'Ro'
    },
    navLinks() {
      return [
        { href: '#top', label: this.$t('nav.start') },
        { href: '#services', label: this.$t('nav.services') },
        { href: '#quality', label: this.$t('nav.quality') },
        { href: '#process', label: this.$t('nav.process') },
        { href: '#partners', label: this.$t('nav.partners') },
        { href: '#answers', label: this.$t('nav.answers') },
        { href: '#info', label: this.$t('nav.info') }
      ]
    }
  },
  mounted() {
    window.addEventListener('resize', this.updateWidth)
    eventBus.triggerLangChange = this.selectLang
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateWidth)
  },
  methods: {
    updateWidth() {
      this.windowWidth = window.innerWidth
    },
    onNavClick(href) {
      this.activeLink = href
      eventBus.sidebarOpen = false
      const isHome = this.$route.path === '/'
      if (href === '#top') {
        if (isHome) {
          window.scrollTo({ top: 0, behavior: 'smooth' })
        } else {
          this.$router.push('/').then(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' })
          })
        }
        return
      }
      if (isHome) {
        const el = document.querySelector(href)
        if (el) el.scrollIntoView({ behavior: 'smooth' })
      } else {
        this.$router.push('/').then(() => {
          setTimeout(() => {
            const el = document.querySelector(href)
            if (el) el.scrollIntoView({ behavior: 'smooth' })
          }, 100)
        })
      }
    },
    onContactClick() {
      eventBus.sidebarOpen = false
      eventBus.showContactModal = true
    },
    selectLang(code) {
      this.langMenuOpen = false
      eventBus.sidebarOpen = false
      this.langOverlay = true
      setTimeout(() => {
        localStorage.setItem('lang', code)
        const localeMap = { Ro: 'ro', En: 'en', Ru: 'ru' }
        i18n.global.locale.value = localeMap[code]
        setTimeout(() => {
          this.langOverlay = false
        }, 250)
      }, 250)
    }
  }
}
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

#app {
  font-family: "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  letter-spacing: -0.01em;
}

html {
  scroll-behavior: smooth;
}

.animate-block {
  opacity: 0;
  transform: translateY(40px);
  transition-property: opacity, transform;
  transition-duration: 0.6s;
  transition-timing-function: ease-out;
}

.animate-block.visible {
  opacity: 1;
  transform: translateY(0);
}

body {
  margin: 0;
  padding: 0;
  background: #ededed;
}

.v-container {
  max-width: 1200px !important;
  padding-left: 40px !important;
  padding-right: 40px !important;
}

@media (max-width: 1280px) {
  .v-container {
    padding-left: 24px !important;
    padding-right: 24px !important;
  }
}

@media (max-width: 600px) {
  .v-container {
    padding-left: 16px !important;
    padding-right: 16px !important;
  }
}

/* Sidebar Styles */
.sidebar-content {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  padding: 40px;
}

.sidebar-close {
  position: absolute;
  top: 32px;
  right: 32px;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #fff;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;
  flex-shrink: 0;
  z-index: 1;
}

.sidebar-close:hover {
  transform: scale(1.05);
}

.sidebar-nav {
  display: flex;
  flex-direction: column;
  gap: 0;
  margin-top: 120px;
}

.sidebar-link {
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  font-size: 35px;
  font-weight: 400;
  line-height: 68px;
  letter-spacing: -0.02em;
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  transition: color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.sidebar-link:hover {
  color: #fff;
}

.sidebar-link.active {
  color: #fff;
}

.sidebar-link.active .sidebar-link-text {
  text-decoration: underline;
  text-decoration-style: solid;
  text-underline-offset: 4px;
}

.sidebar-dot {
  font-size: 12px;
  color: #fff;
  margin-right: 20px;
  text-decoration: none;
  display: inline-block;
}

.sidebar-bottom {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 40px;
}

.sidebar-contact-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 42px;
  padding: 0 32px;
  background: #fff;
  color: #1CA5BE;
  border: none;
  border-radius: 4px;
  font-family: "SF Pro", "SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s ease;
}

.sidebar-contact-btn:hover {
  background: rgba(255, 255, 255, 0.9);
}

.sidebar-lang-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  background: rgba(46, 46, 46, 0.3);
  color: #fff;
  border: none;
  border-radius: 4px;
  font-family: "SF Pro", "SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 15px;
  font-weight: 400;
  cursor: pointer;
  transition: background 0.2s ease;
}

.sidebar-lang-btn:hover {
  background: rgba(46, 46, 46, 0.45);
}

.lang-dropdown {
  background: rgb(46, 46, 46);
  border-radius: 6px;
  padding: 4px 0;
  display: flex;
  flex-direction: column;
  min-width: 130px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
}

.lang-option {
  background: none;
  border: none;
  color: rgb(241, 241, 241);
  font-family: "SF Pro", "SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 15px;
  font-weight: 400;
  text-align: left;
  padding: 10px 22px 10px 25px;
  cursor: pointer;
  transition: background 0.15s ease;
}

.lang-option:hover {
  background: rgba(255, 255, 255, 0.08);
}

.lang-option.active {
  color: rgb(28, 165, 190);
}

.lang-overlay {
  position: fixed;
  inset: 0;
  background: #fff;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.5s ease;
  z-index: 99999;
}

.lang-overlay.visible {
  opacity: 1;
  pointer-events: all;
}
</style>
